import { useState, useEffect, useCallback } from 'react';
import { PopulatedDatabaseQuestion } from '@fake-stack-overflow/shared/types/question';
import { AnswerUpdatePayload } from '@fake-stack-overflow/shared/types/socket';
import { getActivityFeed, ActivityItem } from '../services/activityService';
import useUserContext from './useUserContext';

interface ActivityFeedOptions {
  includeSelf?: boolean;
  includeFollowing?: boolean;
}

interface ActivityFeedState {
  activities: ActivityItem[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  filterOptions: ActivityFeedOptions;
  setFilterOptions: (options: ActivityFeedOptions) => void;
}

/**
 * Custom hook for managing and fetching activity feed data
 */
const useActivityFeed = (initialOptions?: ActivityFeedOptions): ActivityFeedState => {
  const { user, socket } = useUserContext();
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filterOptions, setFilterOptionsState] = useState<ActivityFeedOptions>({
    includeSelf: initialOptions?.includeSelf ?? true,
    includeFollowing: initialOptions?.includeFollowing ?? true,
  });

  const fetchActivityFeed = useCallback(async () => {
    if (!user.username) return;

    setLoading(true);
    setError(null);

    try {
      const activityData = await getActivityFeed(
        user.username,
        filterOptions.includeSelf,
        filterOptions.includeFollowing,
      );
      setActivities(activityData);
    } catch (err) {
      setError('Failed to load activity feed');
    } finally {
      setLoading(false);
    }
  }, [user.username, filterOptions.includeSelf, filterOptions.includeFollowing]);

  // Create a setFilterOptions function that updates state and triggers a re-fetch
  const setFilterOptions = useCallback((options: ActivityFeedOptions) => {
    setFilterOptionsState(prev => ({
      ...prev,
      ...options,
    }));
    // The fetchActivityFeed will be called automatically via the useEffect below
    // because filterOptions is in the dependency array of fetchActivityFeed
  }, []);

  // Fetch activities when component mounts or filter options change
  useEffect(() => {
    if (user.username) {
      fetchActivityFeed();
    }
  }, [fetchActivityFeed, user.username]);

  // Handle socket updates for new questions/answers
  useEffect(() => {
    if (!user || !user.username || !socket) return;

    interface UpdatePayload {
      type: string;
      question?: {
        askedBy: string;
      };
      answer?: {
        ansBy: string;
      };
    }

    // Modified handler with custom type that includes required 'type' field
    const handleQuestionUpdate = (question: PopulatedDatabaseQuestion): void => {
      // The socket receives a question object directly, so we need to simulate our UpdatePayload
      // structure for compatibility with existing logic
      const data: UpdatePayload = {
        type: 'added',
        question: {
          askedBy: question.askedBy,
        },
      };

      if (data.type === 'added' && data.question) {
        // Only refresh if it's a question by user or someone they follow
        if (filterOptions.includeSelf && data.question.askedBy === user.username) {
          fetchActivityFeed();
        } else if (filterOptions.includeFollowing) {
          // Check if the question is by someone the user follows
          const following =
            user.following instanceof Map
              ? user.following
              : new Map(Object.entries(user.following || {}));

          if (following.has(data.question.askedBy)) {
            fetchActivityFeed();
          }
        }
      }
    };

    // Modified handler with custom type that includes required 'type' field
    const handleAnswerUpdate = (result: AnswerUpdatePayload): void => {
      // The socket receives an AnswerUpdatePayload, so we need to simulate our UpdatePayload
      // structure for compatibility with existing logic
      const data: UpdatePayload = {
        type: 'added',
        answer: {
          ansBy: result.answer.ansBy,
        },
      };

      if (data.type === 'added' && data.answer) {
        // Only refresh if it's an answer by user or someone they follow
        if (filterOptions.includeSelf && data.answer.ansBy === user.username) {
          fetchActivityFeed();
        } else if (filterOptions.includeFollowing) {
          // Check if the answer is by someone the user follows
          const following =
            user.following instanceof Map
              ? user.following
              : new Map(Object.entries(user.following || {}));

          if (following.has(data.answer.ansBy)) {
            fetchActivityFeed();
          }
        }
      }
    };

    // Subscribe to socket events
    socket.on('questionUpdate', handleQuestionUpdate);
    socket.on('answerUpdate', handleAnswerUpdate);

    // eslint-disable-next-line consistent-return
    return () => {
      socket.off('questionUpdate', handleQuestionUpdate);
      socket.off('answerUpdate', handleAnswerUpdate);
    };
  }, [user, filterOptions, socket, fetchActivityFeed]);

  return {
    activities,
    loading,
    error,
    refresh: fetchActivityFeed,
    filterOptions,
    setFilterOptions,
  };
};

export default useActivityFeed;
